import {
  createDefaultBookExportConfig,
  type BookExportConfig,
  type BookExportIllustration,
  type BookExportManifestSource,
  type BookExportSectionKey,
  type BookExportSummary,
  type BookExportSummarySection,
  type CanonicalBookExportManifest,
} from "./export-contract.ts";

export const bookExportSectionTitles: Record<BookExportSectionKey, string> = {
  characters_appendix: "Character appendix",
  cover: "Cover",
  end_matter: "End matter",
  lore_appendix: "Lore appendix",
  main_story: "Main story",
  side_stories: "Side stories",
  table_of_contents: "Table of contents",
  timeline_appendix: "Timeline appendix",
  title_page: "Title page",
};

export const bookExportSectionLabelKeys: Record<BookExportSectionKey, string> = {
  characters_appendix: "bookAssembly.summary.sectionLabels.characters_appendix",
  cover: "bookAssembly.summary.sectionLabels.cover",
  end_matter: "bookAssembly.summary.sectionLabels.end_matter",
  lore_appendix: "bookAssembly.summary.sectionLabels.lore_appendix",
  main_story: "bookAssembly.summary.sectionLabels.main_story",
  side_stories: "bookAssembly.summary.sectionLabels.side_stories",
  table_of_contents: "bookAssembly.summary.sectionLabels.table_of_contents",
  timeline_appendix: "bookAssembly.summary.sectionLabels.timeline_appendix",
  title_page: "bookAssembly.summary.sectionLabels.title_page",
};

function normalizeOptionalHtml(value: string | null | undefined) {
  const trimmed = value?.trim() ?? "";
  return trimmed.length > 0 ? trimmed : null;
}

function normalizeOptionalText(value: string | null | undefined) {
  const trimmed = value?.trim() ?? "";
  return trimmed.length > 0 ? trimmed : null;
}

function normalizeIllustrations(
  illustrations: BookExportIllustration[],
  includeIllustrations: boolean,
) {
  if (!includeIllustrations) {
    return [];
  }

  return illustrations.flatMap((illustration) => {
    const assetId = normalizeOptionalText(illustration.asset_id);
    if (!assetId) {
      return [];
    }

    return [{
      ...illustration,
      asset_id: assetId,
      caption: normalizeOptionalText(illustration.caption),
    }];
  });
}

function filterSelectedSideStories(
  source: BookExportManifestSource,
  config: BookExportConfig,
) {
  if (config.exportMode === "story_only") {
    return [];
  }

  const selectedIds = new Set(config.selectedSideStoryIds);
  if (selectedIds.size === 0) {
    return [];
  }

  return source.sideStories.filter((story) => selectedIds.has(story.id));
}

function resolveFrontMatterMessages(
  source: BookExportManifestSource,
  config: BookExportConfig,
) {
  switch (config.frontMatterMessageSource) {
    case "author_note":
      return {
        author_note_html: normalizeOptionalHtml(source.bookMetadata.author_note_html),
        dedication_html: null,
      };
    case "dedication":
      return {
        author_note_html: null,
        dedication_html: normalizeOptionalHtml(source.bookMetadata.dedication_html),
      };
    default:
      return {
        author_note_html: null,
        dedication_html: null,
      };
  }
}

function resolveEndMatterMessage(
  source: BookExportManifestSource,
  config: BookExportConfig,
) {
  switch (config.endMatterMessageSource) {
    case "author_note":
      return normalizeOptionalHtml(source.bookMetadata.author_note_html);
    case "special_thanks":
      return normalizeOptionalHtml(source.bookMetadata.special_thanks_html);
    default:
      return null;
  }
}

function withChapterFallbacks(
  source: BookExportManifestSource,
  includeIllustrations: boolean,
) {
  return source.mainStoryChapters.map((chapter, index) => ({
    ...chapter,
    illustrations: normalizeIllustrations(chapter.illustrations, includeIllustrations),
    title: normalizeOptionalText(chapter.title) ?? `Chapter ${index + 1}`,
  }));
}

function resolveCharacterAppendixEntries(
  source: BookExportManifestSource,
  config: BookExportConfig,
) {
  if (!config.includeCharacterAppendix) {
    return [];
  }

  return source.characterAppendixEntries.map((entry) => ({
    ...entry,
    body_html: normalizeOptionalHtml(entry.body_html) ?? "",
    image_asset_id: config.includeIllustrations
      ? normalizeOptionalText(entry.image_asset_id)
      : null,
    title: normalizeOptionalText(entry.title) ?? "Character entry",
  }));
}

function resolveLoreAppendixEntries(
  source: BookExportManifestSource,
  config: BookExportConfig,
) {
  if (!config.includeLoreAppendix) {
    return [];
  }

  return source.loreAppendixEntries.map((entry) => ({
    ...entry,
    body_html: normalizeOptionalHtml(entry.body_html) ?? "",
    tags: entry.tags.map((tag) => tag.trim()).filter((tag) => tag.length > 0),
    title: normalizeOptionalText(entry.title) ?? "Lore entry",
    type: normalizeOptionalText(entry.type) ?? "reference",
  }));
}

function resolveTimelineAppendixEntries(
  source: BookExportManifestSource,
  config: BookExportConfig,
) {
  if (!config.includeTimelineAppendix) {
    return [];
  }

  return source.timelineAppendixEntries.map((entry) => ({
    ...entry,
    body: normalizeOptionalText(entry.body) ?? "",
    era_label: normalizeOptionalText(entry.era_label),
    title: normalizeOptionalText(entry.title) ?? "Timeline entry",
  }));
}

function buildSummarySection(
  key: BookExportSectionKey,
  included: boolean,
  itemCount: number,
): BookExportSummarySection {
  return {
    key,
    label: getBookExportSectionTitle(key),
    included,
    itemCount,
  };
}

export function getBookExportSectionTitle(key: BookExportSectionKey) {
  return bookExportSectionTitles[key];
}

export function getBookExportSectionLabelKey(key: BookExportSectionKey) {
  return bookExportSectionLabelKeys[key];
}

export function buildPreviewBookExportManifest(input: {
  config: Partial<BookExportConfig>;
  source: BookExportManifestSource;
}): CanonicalBookExportManifest {
  const config = createDefaultBookExportConfig(input.config);
  const mainStoryChapters = withChapterFallbacks(input.source, config.includeIllustrations);
  const selectedSideStories = filterSelectedSideStories(input.source, config);
  const characterAppendixEntries = resolveCharacterAppendixEntries(input.source, config);
  const loreAppendixEntries = resolveLoreAppendixEntries(input.source, config);
  const timelineAppendixEntries = resolveTimelineAppendixEntries(input.source, config);
  const includeMainStory =
    config.exportMode !== "side_stories_only" && mainStoryChapters.length > 0;
  const includeSideStories =
    config.exportMode !== "story_only" && selectedSideStories.length > 0;
  const includeAppendices = config.exportMode === "full_compendium";
  const frontMatter = resolveFrontMatterMessages(input.source, config);

  return {
    project: {
      id: input.source.project.id,
      title: input.source.project.title,
      subtitle: normalizeOptionalText(input.source.project.subtitle),
      author_name: normalizeOptionalText(input.source.project.author_name) ?? "Unknown Author",
      series_name: normalizeOptionalText(input.source.project.series_name),
      volume_label: normalizeOptionalText(input.source.project.volume_label),
      cover_image_path: normalizeOptionalText(input.source.project.cover_image_path),
    },
    export_profile: {
      target: config.target,
      export_mode: config.exportMode,
      preset: config.preset,
      page_size: config.pageSize,
      print_mode: config.printMode,
      mirrored_margins: config.printMode ? config.mirroredMargins : false,
      start_chapters_on_right_page: config.printMode
        ? config.startChaptersOnRightPage
        : false,
      show_page_numbers: config.showPageNumbers,
    },
    front_matter: {
      include_cover: config.includeCover,
      include_title_page: config.includeTitlePage,
      author_note_html: frontMatter.author_note_html,
      dedication_html: frontMatter.dedication_html,
      include_toc: config.includeToc,
    },
    main_story: {
      include: includeMainStory,
      chapters: includeMainStory ? mainStoryChapters : [],
    },
    side_stories: {
      include: includeSideStories,
      stories: includeSideStories ? selectedSideStories : [],
    },
    appendices: {
      characters: {
        include:
          includeAppendices
          && characterAppendixEntries.length > 0,
        entries: includeAppendices ? characterAppendixEntries : [],
      },
      lore: {
        include:
          includeAppendices
          && loreAppendixEntries.length > 0,
        entries: includeAppendices ? loreAppendixEntries : [],
      },
      timeline: {
        include:
          includeAppendices
          && timelineAppendixEntries.length > 0,
        entries: includeAppendices ? timelineAppendixEntries : [],
      },
    },
    end_matter: {
      special_thanks_html: resolveEndMatterMessage(input.source, config),
    },
  };
}

export function collectManifestSectionKeys(
  manifest: CanonicalBookExportManifest,
): BookExportSectionKey[] {
  const sectionKeys: BookExportSectionKey[] = [];

  if (manifest.front_matter.include_cover) {
    sectionKeys.push("cover");
  }

  if (manifest.front_matter.include_title_page) {
    sectionKeys.push("title_page");
  }

  if (manifest.front_matter.include_toc) {
    sectionKeys.push("table_of_contents");
  }

  if (manifest.main_story.include && manifest.main_story.chapters.length > 0) {
    sectionKeys.push("main_story");
  }

  if (manifest.side_stories.include && manifest.side_stories.stories.length > 0) {
    sectionKeys.push("side_stories");
  }

  if (manifest.appendices.characters.include && manifest.appendices.characters.entries.length > 0) {
    sectionKeys.push("characters_appendix");
  }

  if (manifest.appendices.lore.include && manifest.appendices.lore.entries.length > 0) {
    sectionKeys.push("lore_appendix");
  }

  if (manifest.appendices.timeline.include && manifest.appendices.timeline.entries.length > 0) {
    sectionKeys.push("timeline_appendix");
  }

  if (manifest.end_matter.special_thanks_html) {
    sectionKeys.push("end_matter");
  }

  return sectionKeys;
}

export function buildBookExportSummary(input: {
  config: Partial<BookExportConfig>;
  source: BookExportManifestSource;
}): BookExportSummary {
  const manifest = buildPreviewBookExportManifest(input);
  const config = createDefaultBookExportConfig(input.config);
  const warnings: string[] = [];

  if (config.includeCover && !input.source.project.cover_image_path) {
    warnings.push("missing_cover_art");
  }

  if (config.exportMode !== "story_only" && config.selectedSideStoryIds.length === 0) {
    warnings.push("no_side_stories_selected");
  }

  if (config.exportMode === "full_compendium") {
    const hasAppendixSelection =
      config.includeCharacterAppendix
      || config.includeLoreAppendix
      || config.includeTimelineAppendix;

    if (hasAppendixSelection === false) {
      warnings.push("no_appendices_selected");
    }
  }

  return {
    target: config.target,
    exportMode: config.exportMode,
    preset: config.preset,
    pageSize: config.pageSize,
    sections: [
      buildSummarySection(
        "cover",
        manifest.front_matter.include_cover,
        manifest.front_matter.include_cover ? 1 : 0,
      ),
      buildSummarySection(
        "title_page",
        manifest.front_matter.include_title_page,
        manifest.front_matter.include_title_page ? 1 : 0,
      ),
      buildSummarySection(
        "table_of_contents",
        manifest.front_matter.include_toc,
        manifest.front_matter.include_toc ? collectManifestSectionKeys(manifest).length : 0,
      ),
      buildSummarySection(
        "main_story",
        manifest.main_story.include,
        manifest.main_story.chapters.length,
      ),
      buildSummarySection(
        "side_stories",
        manifest.side_stories.include,
        manifest.side_stories.stories.length,
      ),
      buildSummarySection(
        "characters_appendix",
        manifest.appendices.characters.include,
        manifest.appendices.characters.entries.length,
      ),
      buildSummarySection(
        "lore_appendix",
        manifest.appendices.lore.include,
        manifest.appendices.lore.entries.length,
      ),
      buildSummarySection(
        "timeline_appendix",
        manifest.appendices.timeline.include,
        manifest.appendices.timeline.entries.length,
      ),
      buildSummarySection(
        "end_matter",
        Boolean(manifest.end_matter.special_thanks_html),
        manifest.end_matter.special_thanks_html ? 1 : 0,
      ),
    ],
    warnings,
  };
}
